import {
  SKILL_AP_CARD_CASH_COST,
  SKILL_AP_CARD_SKU,
  SKILL_AOE_RANGE_CARD_CASH_COST,
  SKILL_AOE_RANGE_CARD_SKU,
  SKILL_AOE_RANGE_WIDTH_PER_CARD,
  SKILL_AP_PER_CARD,
  SKILL_BUFF_DURATION_CARD_CASH_COST,
  SKILL_BUFF_DURATION_CARD_SKU,
  SKILL_BUFF_DURATION_SEC_PER_CARD,
  SKILL_COOLDOWN_CARD_CASH_COST,
  SKILL_COOLDOWN_CARD_SKU,
  SKILL_COOLDOWN_SEC_PER_CARD,
  SKILL_CREATE_CARD_CASH_COST,
  SKILL_CREATE_CARD_SKU,
  SKILL_LIBRARY_SLOT_CARD_SKU,
  SKILL_SLOT_CARD_SKU,
  skillLibrarySlotUnlockCashCost,
  skillSlotUnlockCashCost,
} from "@/character/skills/constants";
import {
  DUNGEON_FLOOR_CASH_COST,
  DUNGEON_FLOOR_SKU,
} from "@/dungeon/dungeon-meta";
import {
  DUNGEON_MONSTER_POWER_CARD_CASH_COST,
  DUNGEON_MONSTER_POWER_CARD_SKU,
} from "@/dungeon/dungeon-monster-power-card";
import {
  SHADOW_AP_CARD_CASH_COST,
  SHADOW_AP_CARD_SKU,
  SHADOW_AP_PER_CARD,
  SHADOW_CREATE_CARD_CASH_COST,
  SHADOW_CREATE_CARD_SKU,
} from "@/shadow/constants";
import {
  PET_AP_CARD_CASH_COST,
  PET_AP_CARD_SKU,
  PET_AP_PER_CARD,
  PET_CREATE_CARD_CASH_COST,
  PET_CREATE_CARD_SKU,
  PET_SIZE_CARD_CASH_COST,
  PET_SIZE_CARD_SKU,
  PET_SIZE_PERCENT_PER_CARD,
  PET_SKILL_AOE_RANGE_CARD_CASH_COST,
  PET_SKILL_AOE_RANGE_CARD_SKU,
  PET_SKILL_AP_CARD_CASH_COST,
  PET_SKILL_AP_CARD_SKU,
  PET_SKILL_BUFF_DURATION_CARD_CASH_COST,
  PET_SKILL_BUFF_DURATION_CARD_SKU,
  PET_SKILL_COOLDOWN_CARD_CASH_COST,
  PET_SKILL_COOLDOWN_CARD_SKU,
  PET_SKILL_CREATE_CARD_CASH_COST,
  PET_SKILL_CREATE_CARD_SKU,
} from "@/pet/constants";
import { getGameSettings } from "@/game/model/game-settings";
import {
  HOME_STRUCTURE_DEF_CARD_CASH_COST,
  HOME_STRUCTURE_DEF_CARD_SKU,
  HOME_STRUCTURE_HP_CARD_CASH_COST,
  HOME_STRUCTURE_HP_CARD_SKU,
  HOME_STRUCTURE_HP_POTION_SKU,
  HOME_STRUCTURE_REGEN_CARD_CASH_COST,
  HOME_STRUCTURE_REGEN_CARD_SKU,
} from "@/home/home-structure-types";
import type { HomePartKind } from "@/home/part-kind";
import {
  fulfillmentForTier,
  HEALTH_POTION_DEFAULT_COIN_PRICE,
  HEALTH_POTION_HEAL_BY_TIER,
  HEALTH_POTION_LABEL_TH,
  HEALTH_POTION_SKU_BY_TIER,
  HEALTH_POTION_TIERS,
  stashItemIdForTier,
  tierFromFulfillment,
  type HealthPotionFulfillmentKind,
  type HealthPotionTier,
} from "@/home/health-potions";
import {
  fulfillmentForManaTier,
  MANA_POTION_DEFAULT_COIN_PRICE,
  MANA_POTION_LABEL_TH,
  MANA_POTION_RESTORE_BY_TIER,
  MANA_POTION_SKU_BY_TIER,
  MANA_POTION_TIERS,
  stashItemIdForManaTier,
  tierFromManaFulfillment,
  type ManaPotionFulfillmentKind,
} from "@/home/mana-potions";

/** Wallet currency used in GM / home shop rows. */
export type ShopCurrency = "coins" | "gems" | "cash";

/** Purchase log `kind` — aligned with GM shop fulfillment grants. */
export type ShopPurchaseKind =
  | "slot_pack"
  | "gem_pack"
  | "coin_pack"
  | "health_potion"
  | "mana_potion"
  | "skill_slot_card"
  | "skill_library_slot_card"
  | "skill_create_card"
  | "skill_cooldown_card"
  | "skill_ap_card"
  | "skill_buff_duration_card"
  | "skill_aoe_range_card"
  | "revive_card"
  | "dungeon_floor_slot"
  | "dungeon_monster_power_card"
  | "shadow_create_card"
  | "shadow_ap_card"
  | "pet_create_card"
  | "pet_ap_card"
  | "pet_size_card"
  | "pet_skill_create_card"
  | "pet_skill_cooldown_card"
  | "pet_skill_ap_card"
  | "pet_skill_buff_duration_card"
  | "pet_skill_aoe_range_card"
  | "home_structure_hp_card"
  | "home_structure_def_card"
  | "home_structure_regen_card"
  | "home_structure_hp_potion";

export type ShopFulfillmentKind =
  | HealthPotionFulfillmentKind
  | ManaPotionFulfillmentKind
  | "revive_card"
  | "skill_slot_card"
  | "skill_library_slot_card"
  | "skill_create_card"
  | "skill_cooldown_card"
  | "skill_ap_card"
  | "skill_buff_duration_card"
  | "skill_aoe_range_card"
  | "catalog_slot_pack"
  | "dungeon_floor_slot"
  | "dungeon_monster_power_card"
  | "shadow_create_card"
  | "shadow_ap_card"
  | "pet_create_card"
  | "pet_ap_card"
  | "pet_size_card"
  | "pet_skill_create_card"
  | "pet_skill_cooldown_card"
  | "pet_skill_ap_card"
  | "pet_skill_buff_duration_card"
  | "pet_skill_aoe_range_card"
  | "home_structure_hp_card"
  | "home_structure_def_card"
  | "home_structure_regen_card"
  | "home_structure_hp_potion";

export const SHOP_CURRENCY_LABELS: Record<ShopCurrency, string> = {
  coins: "ทอง",
  gems: "เพชร",
  cash: "Cash",
};

export const CATALOG_SLOT_PACK_SKU = "home.catalog_slot_pack.v1";
export const CATALOG_SLOT_PACK_SIZE = 5;
export const CATALOG_SLOT_PACK_GEM_COST = 50;

export const REVIVE_CARD_SKU = "character.revive_card.v1";
export const REVIVE_CARD_CASH_COST = 150;

export type ShopStashItemId =
  | "health_potion_small"
  | "health_potion_medium"
  | "health_potion_large"
  | "mana_potion_small"
  | "mana_potion_medium"
  | "mana_potion_large"
  | "revive_card"
  | "skill_cooldown_card"
  | "skill_ap_card"
  | "skill_buff_duration_card"
  | "skill_aoe_range_card"
  | "skill_create_card"
  | "shadow_create_card"
  | "shadow_ap_card"
  | "pet_create_card"
  | "pet_ap_card"
  | "pet_size_card"
  | "pet_skill_create_card"
  | "pet_skill_cooldown_card"
  | "pet_skill_ap_card"
  | "pet_skill_buff_duration_card"
  | "pet_skill_aoe_range_card"
  | "home_structure_hp_card"
  | "home_structure_def_card"
  | "home_structure_regen_card"
  | "home_structure_hp_potion";

type ShopProductCore = {
  fulfillment: ShopFulfillmentKind;
  purchaseKind: ShopPurchaseKind;
  defaultCatalogId: string;
  defaultCurrency: ShopCurrency;
  defaultSortOrder: number;
  gmLabel: string;
  defaultName: string;
  defaultDescription: string;
  stashItemId?: ShopStashItemId;
  sku: string;
  defaultPrice: number;
  catalogPartKind?: HomePartKind;
  catalogSlotsGranted?: number;
};

function potionProducts(): ShopProductCore[] {
  return HEALTH_POTION_TIERS.map((tier) => ({
    fulfillment: fulfillmentForTier(tier),
    purchaseKind: "health_potion" as const,
    defaultCatalogId: `default-health-potion-${tier}`,
    defaultCurrency: "coins" as const,
    defaultSortOrder: tier === "small" ? 10 : tier === "medium" ? 11 : 12,
    gmLabel: `${HEALTH_POTION_LABEL_TH[tier]} (+${HEALTH_POTION_HEAL_BY_TIER[tier]} HP)`,
    defaultName: HEALTH_POTION_LABEL_TH[tier],
    defaultDescription: `ฟื้น +${HEALTH_POTION_HEAL_BY_TIER[tier]} HP`,
    stashItemId: stashItemIdForTier(tier),
    sku: HEALTH_POTION_SKU_BY_TIER[tier],
    defaultPrice: HEALTH_POTION_DEFAULT_COIN_PRICE[tier],
  }));
}

function manaPotionProducts(): ShopProductCore[] {
  return MANA_POTION_TIERS.map((tier) => ({
    fulfillment: fulfillmentForManaTier(tier),
    purchaseKind: "mana_potion" as const,
    defaultCatalogId: `default-mana-potion-${tier}`,
    defaultCurrency: "coins" as const,
    defaultSortOrder: tier === "small" ? 13 : tier === "medium" ? 14 : 15,
    gmLabel: `${MANA_POTION_LABEL_TH[tier]} (+${MANA_POTION_RESTORE_BY_TIER[tier]} MP)`,
    defaultName: MANA_POTION_LABEL_TH[tier],
    defaultDescription: `ฟื้น +${MANA_POTION_RESTORE_BY_TIER[tier]} มานา`,
    stashItemId: stashItemIdForManaTier(tier),
    sku: MANA_POTION_SKU_BY_TIER[tier],
    defaultPrice: MANA_POTION_DEFAULT_COIN_PRICE[tier],
  }));
}

const NON_POTION_SHOP_PRODUCTS: ShopProductCore[] = [
  {
    fulfillment: "catalog_slot_pack",
    purchaseKind: "slot_pack",
    defaultCatalogId: "default-catalog-slots",
    defaultCurrency: "gems",
    defaultSortOrder: 20,
    gmLabel: "ช่องคลังแต่งบ้าน",
    defaultName: "ช่องคลังแต่งบ้าน",
    defaultDescription: `+${CATALOG_SLOT_PACK_SIZE} ช่องต่อแพ็ก (เลือกหมวดตอนซื้อ)`,
    sku: CATALOG_SLOT_PACK_SKU,
    defaultPrice: CATALOG_SLOT_PACK_GEM_COST,
    catalogPartKind: "prop",
    catalogSlotsGranted: CATALOG_SLOT_PACK_SIZE,
  },
  {
    fulfillment: "skill_library_slot_card",
    purchaseKind: "skill_library_slot_card",
    defaultCatalogId: "default-skill-library-slot",
    defaultCurrency: "cash",
    defaultSortOrder: 25,
    gmLabel: "ช่องคลังสกิล (+1)",
    defaultName: "บัตรเพิ่มช่องคลังสกิล",
    defaultDescription: "เก็บสกิลในคลังได้อีก 1 ช่อง (เริ่มต้น 3 ช่อง)",
    sku: SKILL_LIBRARY_SLOT_CARD_SKU,
    defaultPrice: skillLibrarySlotUnlockCashCost(4) ?? 300,
  },
  {
    fulfillment: "skill_create_card",
    purchaseKind: "skill_create_card",
    defaultCatalogId: "default-skill-create",
    defaultCurrency: "cash",
    defaultSortOrder: 26,
    gmLabel: "บัตรสร้างสกิล (+1)",
    defaultName: "บัตรสร้างสกิล",
    defaultDescription: "ใช้สร้างสกิลใหม่ในคลัง (ลบแล้วต้องซื้อใหม่)",
    stashItemId: "skill_create_card",
    sku: SKILL_CREATE_CARD_SKU,
    defaultPrice: SKILL_CREATE_CARD_CASH_COST,
  },
  {
    fulfillment: "skill_slot_card",
    purchaseKind: "skill_slot_card",
    defaultCatalogId: "default-skill-slot",
    defaultCurrency: "cash",
    defaultSortOrder: 30,
    gmLabel: "ช่องสกิล (+1)",
    defaultName: "บัตรเพิ่มช่องสกิล",
    defaultDescription: "เปิดช่องสกิลเพิ่ม 1 ช่อง",
    sku: SKILL_SLOT_CARD_SKU,
    defaultPrice: skillSlotUnlockCashCost(4) ?? 500,
  },
  {
    fulfillment: "skill_cooldown_card",
    purchaseKind: "skill_cooldown_card",
    defaultCatalogId: "default-skill-cd",
    defaultCurrency: "cash",
    defaultSortOrder: 40,
    gmLabel: "บัตรลดคูลดาวน์สกิล (+1)",
    defaultName: "บัตรลดคูลดาวน์สกิล",
    defaultDescription: `ลดคูลดาวน์สกิล ${SKILL_COOLDOWN_SEC_PER_CARD} วินาทีต่อใบ (ติดที่คลัง)`,
    stashItemId: "skill_cooldown_card",
    sku: SKILL_COOLDOWN_CARD_SKU,
    defaultPrice: SKILL_COOLDOWN_CARD_CASH_COST,
  },
  {
    fulfillment: "skill_ap_card",
    purchaseKind: "skill_ap_card",
    defaultCatalogId: "default-skill-ap",
    defaultCurrency: "cash",
    defaultSortOrder: 45,
    gmLabel: `บัตร AP สกิลบัฟ (+${SKILL_AP_PER_CARD} AP/ใบ)`,
    defaultName: "บัตร AP สกิลบัฟ",
    defaultDescription: `เพิ่มเพดาน AP ของสกิลบัฟ +${SKILL_AP_PER_CARD} ต่อใบ (ใช้ที่คลังสกิล)`,
    stashItemId: "skill_ap_card",
    sku: SKILL_AP_CARD_SKU,
    defaultPrice: SKILL_AP_CARD_CASH_COST,
  },
  {
    fulfillment: "skill_buff_duration_card",
    purchaseKind: "skill_buff_duration_card",
    defaultCatalogId: "default-skill-buff-duration",
    defaultCurrency: "cash",
    defaultSortOrder: 46,
    gmLabel: `บัตรเวลาบัฟ (+${SKILL_BUFF_DURATION_SEC_PER_CARD} วิ/ใบ)`,
    defaultName: "บัตรเวลาบัฟ",
    defaultDescription: `เพิ่มระยะบัฟ +${SKILL_BUFF_DURATION_SEC_PER_CARD} วินาทีต่อใบ (ใช้ที่คลังสกิล)`,
    stashItemId: "skill_buff_duration_card",
    sku: SKILL_BUFF_DURATION_CARD_SKU,
    defaultPrice: SKILL_BUFF_DURATION_CARD_CASH_COST,
  },
  {
    fulfillment: "skill_aoe_range_card",
    purchaseKind: "skill_aoe_range_card",
    defaultCatalogId: "default-skill-aoe-range",
    defaultCurrency: "cash",
    defaultSortOrder: 47,
    gmLabel: `บัตรระยะสกิลหมู่ (+${SKILL_AOE_RANGE_WIDTH_PER_CARD * 100}%/ใบ)`,
    defaultName: "บัตรระยะสกิลหมู่",
    defaultDescription: `ขยายสกิลโจมตีหมู่ +${SKILL_AOE_RANGE_WIDTH_PER_CARD * 100}% ต่อใบ (ใช้ที่คลังสกิล)`,
    stashItemId: "skill_aoe_range_card",
    sku: SKILL_AOE_RANGE_CARD_SKU,
    defaultPrice: SKILL_AOE_RANGE_CARD_CASH_COST,
  },
  {
    fulfillment: "shadow_create_card",
    purchaseKind: "shadow_create_card",
    defaultCatalogId: "default-shadow-create",
    defaultCurrency: "cash",
    defaultSortOrder: 48,
    gmLabel: "บัตรสร้างร่างเงา (+1)",
    defaultName: "บัตรสร้างร่างเงา",
    defaultDescription: "สร้างร่างเงาใหม่ 1 ตัว (อัปโหลดสไปรต์ชีตที่แท็บร่างเงา)",
    stashItemId: "shadow_create_card",
    sku: SHADOW_CREATE_CARD_SKU,
    defaultPrice: SHADOW_CREATE_CARD_CASH_COST,
  },
  {
    fulfillment: "shadow_ap_card",
    purchaseKind: "shadow_ap_card",
    defaultCatalogId: "default-shadow-ap",
    defaultCurrency: "cash",
    defaultSortOrder: 49,
    gmLabel: `บัตร AP ร่างเงา (+${SHADOW_AP_PER_CARD} AP/ใบ)`,
    defaultName: "บัตร AP ร่างเงา",
    defaultDescription: `เพิ่ม AP ให้ร่างเงา +${SHADOW_AP_PER_CARD} ต่อใบ (ใช้ที่แท็บร่างเงา)`,
    stashItemId: "shadow_ap_card",
    sku: SHADOW_AP_CARD_SKU,
    defaultPrice: SHADOW_AP_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_create_card",
    purchaseKind: "pet_create_card",
    defaultCatalogId: "default-pet-create",
    defaultCurrency: "cash",
    defaultSortOrder: 49.1,
    gmLabel: "บัตรสร้างสัตว์เลี้ยง (+1)",
    defaultName: "บัตรสร้างสัตว์เลี้ยง",
    defaultDescription: "สร้างสัตว์เลี้ยงใหม่ 1 ตัว (แท็บสัตว์เลี้ยงในเมนูตัวละคร)",
    stashItemId: "pet_create_card",
    sku: PET_CREATE_CARD_SKU,
    defaultPrice: PET_CREATE_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_ap_card",
    purchaseKind: "pet_ap_card",
    defaultCatalogId: "default-pet-ap",
    defaultCurrency: "cash",
    defaultSortOrder: 49.2,
    gmLabel: `บัตร AP สัตว์เลี้ยง (+${PET_AP_PER_CARD} AP/ใบ)`,
    defaultName: "บัตร AP สัตว์เลี้ยง",
    defaultDescription: `เพิ่ม AP สเตตสัตว์เลี้ยง +${PET_AP_PER_CARD} ต่อใบ`,
    stashItemId: "pet_ap_card",
    sku: PET_AP_CARD_SKU,
    defaultPrice: PET_AP_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_size_card",
    purchaseKind: "pet_size_card",
    defaultCatalogId: "default-pet-size",
    defaultCurrency: "cash",
    defaultSortOrder: 49.3,
    gmLabel: `บัตรไซต์สัตว์เลี้ยง (+${PET_SIZE_PERCENT_PER_CARD}%/ใบ)`,
    defaultName: "บัตรไซต์สัตว์เลี้ยง",
    defaultDescription: `ขยายสัตว์เลี้ยง +${PET_SIZE_PERCENT_PER_CARD}% ต่อใบ (ใช้ที่แท็บสัตว์เลี้ยง)`,
    stashItemId: "pet_size_card",
    sku: PET_SIZE_CARD_SKU,
    defaultPrice: PET_SIZE_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_skill_create_card",
    purchaseKind: "pet_skill_create_card",
    defaultCatalogId: "default-pet-skill-create",
    defaultCurrency: "cash",
    defaultSortOrder: 49.4,
    gmLabel: "บัตรสร้างสกิลสัตว์เลี้ยง (+1)",
    defaultName: "บัตรสร้างสกิลสัตว์เลี้ยง",
    defaultDescription: "สร้างสกิลใหม่ในคลังสกิลของสัตว์เลี้ยง (โจมตี/บัฟ)",
    stashItemId: "pet_skill_create_card",
    sku: PET_SKILL_CREATE_CARD_SKU,
    defaultPrice: PET_SKILL_CREATE_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_skill_cooldown_card",
    purchaseKind: "pet_skill_cooldown_card",
    defaultCatalogId: "default-pet-skill-cooldown",
    defaultCurrency: "cash",
    defaultSortOrder: 49.5,
    gmLabel: `บัตรคูลดาวน์สกิลสัตว์ (−${SKILL_COOLDOWN_SEC_PER_CARD}วิ/ใบ)`,
    defaultName: "บัตรคูลดาวน์สกิลสัตว์",
    defaultDescription: "ลดคูลดาวน์สกิลสัตว์เลี้ยง (ใช้ที่คลังสกิลสัตว์)",
    stashItemId: "pet_skill_cooldown_card",
    sku: PET_SKILL_COOLDOWN_CARD_SKU,
    defaultPrice: PET_SKILL_COOLDOWN_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_skill_ap_card",
    purchaseKind: "pet_skill_ap_card",
    defaultCatalogId: "default-pet-skill-ap",
    defaultCurrency: "cash",
    defaultSortOrder: 49.6,
    gmLabel: `บัตร AP สกิลบัฟสัตว์ (+${SKILL_AP_PER_CARD} AP/ใบ)`,
    defaultName: "บัตร AP สกิลบัฟสัตว์",
    defaultDescription: "เพิ่ม AP ให้สกิลบัฟของสัตว์เลี้ยง",
    stashItemId: "pet_skill_ap_card",
    sku: PET_SKILL_AP_CARD_SKU,
    defaultPrice: PET_SKILL_AP_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_skill_buff_duration_card",
    purchaseKind: "pet_skill_buff_duration_card",
    defaultCatalogId: "default-pet-skill-buff-duration",
    defaultCurrency: "cash",
    defaultSortOrder: 49.7,
    gmLabel: `บัตรเวลาบัฟสกิลสัตว์ (+${SKILL_BUFF_DURATION_SEC_PER_CARD}วิ/ใบ)`,
    defaultName: "บัตรเวลาบัฟสกิลสัตว์",
    defaultDescription: "เพิ่มระยะบัฟสกิลสัตว์เลี้ยง",
    stashItemId: "pet_skill_buff_duration_card",
    sku: PET_SKILL_BUFF_DURATION_CARD_SKU,
    defaultPrice: PET_SKILL_BUFF_DURATION_CARD_CASH_COST,
  },
  {
    fulfillment: "pet_skill_aoe_range_card",
    purchaseKind: "pet_skill_aoe_range_card",
    defaultCatalogId: "default-pet-skill-aoe-range",
    defaultCurrency: "cash",
    defaultSortOrder: 49.8,
    gmLabel: `บัตรระยะสกิลหมู่สัตว์ (+${SKILL_AOE_RANGE_WIDTH_PER_CARD * 100}%/ใบ)`,
    defaultName: "บัตรระยะสกิลหมู่สัตว์",
    defaultDescription: "ขยายสกิลโจมตีหมู่ของสัตว์เลี้ยง",
    stashItemId: "pet_skill_aoe_range_card",
    sku: PET_SKILL_AOE_RANGE_CARD_SKU,
    defaultPrice: PET_SKILL_AOE_RANGE_CARD_CASH_COST,
  },
  {
    fulfillment: "revive_card",
    purchaseKind: "revive_card",
    defaultCatalogId: "default-revive",
    defaultCurrency: "cash",
    defaultSortOrder: 50,
    gmLabel: "บัตรชุบชีวิต (+1)",
    defaultName: "บัตรชุบชีวิต",
    defaultDescription: "ใช้เมื่อล้มในหอคอย",
    stashItemId: "revive_card",
    sku: REVIVE_CARD_SKU,
    defaultPrice: REVIVE_CARD_CASH_COST,
  },
  {
    fulfillment: "dungeon_floor_slot",
    purchaseKind: "dungeon_floor_slot",
    defaultCatalogId: "default-dungeon-floor",
    defaultCurrency: "cash",
    defaultSortOrder: 55,
    gmLabel: "ชั้นด่านป้องกัน (+1)",
    defaultName: "ชั้นด่านดันเจียน",
    defaultDescription: "เพิ่มชั้นด่านป้องกันที่แก้ไขได้ (ใช้ที่หน้าด่าน)",
    sku: DUNGEON_FLOOR_SKU,
    defaultPrice: DUNGEON_FLOOR_CASH_COST,
  },
  {
    fulfillment: "dungeon_monster_power_card",
    purchaseKind: "dungeon_monster_power_card",
    defaultCatalogId: "default-dungeon-monster-power",
    defaultCurrency: "cash",
    defaultSortOrder: 56,
    gmLabel: "บัตรคูณพลังมอนสเตอร์ (+1×)",
    defaultName: "บัตรคูณพลังมอนสเตอร์",
    defaultDescription: "ใช้ที่หน้าแก้ด่าน — เพิ่มคูณพลังมอนสเตอร์ +1× ต่อใบ",
    sku: DUNGEON_MONSTER_POWER_CARD_SKU,
    defaultPrice: DUNGEON_MONSTER_POWER_CARD_CASH_COST,
  },
  {
    fulfillment: "home_structure_hp_card",
    purchaseKind: "home_structure_hp_card",
    defaultCatalogId: "default-home-structure-hp-card",
    defaultCurrency: "cash",
    defaultSortOrder: 57,
    gmLabel: "บัตร HP บ้าน (+1 ใบอัพ)",
    defaultName: "บัตร HP บ้าน",
    defaultDescription: "ใช้ที่หน้าสเตตัสบ้าน — เพิ่ม Max HP บ้าน",
    stashItemId: "home_structure_hp_card",
    sku: HOME_STRUCTURE_HP_CARD_SKU,
    defaultPrice: HOME_STRUCTURE_HP_CARD_CASH_COST,
  },
  {
    fulfillment: "home_structure_def_card",
    purchaseKind: "home_structure_def_card",
    defaultCatalogId: "default-home-structure-def-card",
    defaultCurrency: "cash",
    defaultSortOrder: 58,
    gmLabel: "บัตรป้องกันบ้าน (+1 ใบอัพ)",
    defaultName: "บัตรป้องกันบ้าน",
    defaultDescription: "ใช้ที่หน้าสเตตัสบ้าน — เพิ่มป้องกันบ้าน",
    stashItemId: "home_structure_def_card",
    sku: HOME_STRUCTURE_DEF_CARD_SKU,
    defaultPrice: HOME_STRUCTURE_DEF_CARD_CASH_COST,
  },
  {
    fulfillment: "home_structure_regen_card",
    purchaseKind: "home_structure_regen_card",
    defaultCatalogId: "default-home-structure-regen-card",
    defaultCurrency: "cash",
    defaultSortOrder: 59,
    gmLabel: "บัตรฟื้นบ้าน (+1 ใบอัพ)",
    defaultName: "บัตรฟื้นบ้าน",
    defaultDescription: "ใช้ที่หน้าสเตตัสบ้าน — เพิ่มฟื้น HP บ้านต่อวิ",
    stashItemId: "home_structure_regen_card",
    sku: HOME_STRUCTURE_REGEN_CARD_SKU,
    defaultPrice: HOME_STRUCTURE_REGEN_CARD_CASH_COST,
  },
  {
    fulfillment: "home_structure_hp_potion",
    purchaseKind: "home_structure_hp_potion",
    defaultCatalogId: "default-home-structure-hp-potion",
    defaultCurrency: "coins",
    defaultSortOrder: 16,
    gmLabel: `ยา HP บ้าน (+${getGameSettings().homeStructurePotionHeal} HP)`,
    defaultName: "ยา HP บ้าน",
    defaultDescription: `ฟื้น HP บ้าน +${getGameSettings().homeStructurePotionHeal} · ออโต้ได้เมื่อถูกโจมตี`,
    stashItemId: "home_structure_hp_potion",
    sku: HOME_STRUCTURE_HP_POTION_SKU,
    defaultPrice: getGameSettings().homeStructurePotionCoinPrice,
  },
];

/** Canonical link: fulfillment ↔ SKU ↔ purchase kind ↔ stash ↔ default GM row. */
export const SHOP_PRODUCTS: readonly ShopProductCore[] = [
  ...potionProducts(),
  ...manaPotionProducts(),
  ...NON_POTION_SHOP_PRODUCTS,
];

const productByFulfillment = new Map(
  SHOP_PRODUCTS.map((entry) => [entry.fulfillment, entry]),
);

const productByCatalogId = new Map(
  SHOP_PRODUCTS.map((entry) => [entry.defaultCatalogId, entry]),
);

const productByStashId = new Map(
  SHOP_PRODUCTS.filter((entry) => entry.stashItemId).map((entry) => [
    entry.stashItemId!,
    entry,
  ]),
);

export const SHOP_FULFILLMENT_LABELS: Record<ShopFulfillmentKind, string> = {
  ...(Object.fromEntries(SHOP_PRODUCTS.map((p) => [p.fulfillment, p.gmLabel])) as Record<
    ShopFulfillmentKind,
    string
  >),
  health_potion: `ยาเล็ก (+${HEALTH_POTION_HEAL_BY_TIER.small} HP)`,
};

/** Dropdown in GM shop editor (legacy `health_potion` omitted). */
export const SHOP_FULFILLMENT_EDITOR_ORDER: ShopFulfillmentKind[] = [
  "health_potion_small",
  "health_potion_medium",
  "health_potion_large",
  "mana_potion_small",
  "mana_potion_medium",
  "mana_potion_large",
  "catalog_slot_pack",
  "skill_library_slot_card",
  "skill_create_card",
  "skill_slot_card",
  "skill_cooldown_card",
  "skill_ap_card",
  "skill_buff_duration_card",
  "skill_aoe_range_card",
  "shadow_create_card",
  "shadow_ap_card",
  "revive_card",
  "dungeon_floor_slot",
  "dungeon_monster_power_card",
  "home_structure_hp_card",
  "home_structure_def_card",
  "home_structure_regen_card",
  "home_structure_hp_potion",
];

export const SHOP_FULFILLMENT_KIND_SET = new Set<string>([
  "health_potion",
  ...SHOP_PRODUCTS.map((p) => p.fulfillment),
]);

export function shopProductForFulfillment(
  fulfillment: ShopFulfillmentKind,
): ShopProductCore | undefined {
  const healthTier = tierFromFulfillment(fulfillment);
  if (healthTier) return productByFulfillment.get(fulfillmentForTier(healthTier));
  const manaTier = tierFromManaFulfillment(fulfillment);
  if (manaTier) return productByFulfillment.get(fulfillmentForManaTier(manaTier));
  return productByFulfillment.get(fulfillment);
}

export function shopProductForCatalogId(catalogId: string): ShopProductCore | undefined {
  return productByCatalogId.get(catalogId);
}

export function shopProductForStashItemId(
  stashItemId: ShopStashItemId,
): ShopProductCore | undefined {
  return productByStashId.get(stashItemId);
}

export function shopPurchaseKindForFulfillment(
  fulfillment: ShopFulfillmentKind,
): ShopPurchaseKind {
  return shopProductForFulfillment(fulfillment)?.purchaseKind ?? "slot_pack";
}

export function shopSkuForFulfillment(fulfillment: ShopFulfillmentKind): string {
  const product = shopProductForFulfillment(fulfillment);
  if (product) return product.sku;
  const tier = tierFromFulfillment(fulfillment);
  if (tier) return HEALTH_POTION_SKU_BY_TIER[tier];
  return CATALOG_SLOT_PACK_SKU;
}

export function shopStashItemIdForFulfillment(
  fulfillment: ShopFulfillmentKind,
): ShopStashItemId | undefined {
  return shopProductForFulfillment(fulfillment)?.stashItemId;
}

export type DefaultShopCatalogRow = {
  id: string;
  name: string;
  description: string;
  currency: ShopCurrency;
  price: number;
  fulfillment: ShopFulfillmentKind;
  sortOrder: number;
  catalogPartKind?: HomePartKind;
  catalogSlotsGranted?: number;
};

export function buildDefaultShopCatalogRows(): DefaultShopCatalogRow[] {
  return SHOP_PRODUCTS.map((product) => ({
    id: product.defaultCatalogId,
    name: product.defaultName,
    description: product.defaultDescription,
    currency: product.defaultCurrency,
    price: product.defaultPrice,
    fulfillment: product.fulfillment,
    sortOrder: product.defaultSortOrder,
    catalogPartKind: product.catalogPartKind,
    catalogSlotsGranted: product.catalogSlotsGranted,
  }));
}

/** Stash UI for non-potion items (potions built from tiers in stash modal). */
export const SHOP_STASH_CARD_ENTRIES: readonly {
  stashItemId: ShopStashItemId;
  fulfillment: ShopFulfillmentKind;
  name: string;
  hint: string;
  shopTab: ShopCurrency;
  tier: "common" | "rare" | "epic";
}[] = [
  {
    stashItemId: "revive_card",
    fulfillment: "revive_card",
    name: "บัตรชุบชีวิต",
    hint: "ใช้ในหอคอย",
    shopTab: "cash",
    tier: "epic",
  },
  {
    stashItemId: "skill_create_card",
    fulfillment: "skill_create_card",
    name: "บัตรสร้างสกิล",
    hint: "ใช้ตอนสร้างสกิลใหม่",
    shopTab: "cash",
    tier: "rare",
  },
  {
    stashItemId: "shadow_create_card",
    fulfillment: "shadow_create_card",
    name: "บัตรสร้างร่างเงา",
    hint: "ใช้ที่แท็บร่างเงา",
    shopTab: "cash",
    tier: "epic",
  },
  {
    stashItemId: "shadow_ap_card",
    fulfillment: "shadow_ap_card",
    name: "บัตร AP ร่างเงา",
    hint: "ใช้ที่แท็บร่างเงา",
    shopTab: "cash",
    tier: "rare",
  },
  {
    stashItemId: "skill_cooldown_card",
    fulfillment: "skill_cooldown_card",
    name: "บัตรลดคูลดาวน์",
    hint: "ติดคลังสกิล",
    shopTab: "cash",
    tier: "rare",
  },
  {
    stashItemId: "skill_ap_card",
    fulfillment: "skill_ap_card",
    name: "บัตร AP สกิลบัฟ",
    hint: "ใช้ที่คลังสกิลบัฟ",
    shopTab: "cash",
    tier: "rare",
  },
  {
    stashItemId: "skill_buff_duration_card",
    fulfillment: "skill_buff_duration_card",
    name: "บัตรเวลาบัฟ",
    hint: "ใช้ที่คลังสกิลบัฟ",
    shopTab: "cash",
    tier: "rare",
  },
  {
    stashItemId: "skill_aoe_range_card",
    fulfillment: "skill_aoe_range_card",
    name: "บัตรระยะสกิลหมู่",
    hint: "ใช้ที่คลังสกิลโจมตีหมู่",
    shopTab: "cash",
    tier: "rare",
  },
];

export function potionStashTier(tier: HealthPotionTier): "common" | "rare" | "epic" {
  if (tier === "large") return "epic";
  if (tier === "medium") return "rare";
  return "common";
}
